import { Trans, useTranslation } from 'react-i18next';
import { WHATSAPP_SENDER_NAME } from '../config';

// Advisory shown just before the user submits a phone number and
// receives their first WhatsApp message from us. Meta won't approve
// "WallaWhats" as a display name (contains "Whats"), so the sender
// shows up as something unbranded (e.g. "BWS Notifications") — users
// need to know which conversation to look for.
//
// The component renders a one-line text note + a small screenshot
// showing the real inbox preview (icon + sender name + sample OTP
// text) so the unfamiliar sender name never surprises a new user.
// Both pieces sit inside the same backgrounded section so the hint
// reads as a single visual unit.
//
// Uses <Trans> (not dangerouslySetInnerHTML) so the <strong> stays a
// real DOM node and the interpolation is React-escaped end to end.
// Namespace defaults to 'auth'; pass 'dashboard' for the channel
// form on ChannelsPage.
export function WhatsAppSenderHint({ ns = 'auth' }: { ns?: 'auth' | 'dashboard' }) {
  const { t: _ignoredButLoadsBundle } = useTranslation(ns);
  void _ignoredButLoadsBundle;
  const i18nKey = ns === 'auth' ? 'signIn.whatsappSenderHint' : 'channels.whatsappSenderHint';
  const fallback = 'Message will come from <strong>{{sender}}</strong> on WhatsApp';

  return (
    <div
      role="note"
      className="rounded-lg border border-primary-100 bg-primary-50/60 px-3 py-2.5 text-xs text-gray-700"
    >
      <div className="flex items-start gap-2">
        <span aria-hidden="true" className="mt-[1px] leading-none">💬</span>
        <span>
          <Trans
            ns={ns}
            i18nKey={i18nKey}
            defaults={fallback}
            values={{ sender: WHATSAPP_SENDER_NAME }}
            components={{ strong: <strong /> }}
          />
        </span>
      </div>
      <img
        // Real WhatsApp inbox preview — the icon + "BWS Notifications"
        // sender name + sample OTP text. Reassures users that the
        // unbranded sender is the right conversation to open.
        src="/assets/whatsapp/sender-preview.jpg"
        alt={`Preview: a WhatsApp message from ${WHATSAPP_SENDER_NAME}`}
        loading="lazy"
        className="mt-2 block w-full max-w-xs rounded-md border border-gray-200 bg-black"
      />
    </div>
  );
}
